import type { KnowledgeScope } from "@yazamco/shared-types";

export interface AiAgent {
  id: string;
  name: string;
  description: string;
  instructions: string;
  model: string;
  isActive: boolean;
  /**
   * Pins every turn under this agent to one Microsoft 365 source. Null leaves
   * the composer's own scope selector in charge. Arrived with migration 0059,
   * from the thinking model this record absorbed.
   */
  knowledgeScope: KnowledgeScope | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentKnowledge {
  id: string;
  agentId: string;
  sourceType: "text" | "file";
  title: string;
  content: string;
  fileName?: string;
  mimeType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgentTool {
  id: string;
  agentId: string;
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  webhookUrl: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headersTemplate: Record<string, unknown>;
  payloadTemplate: Record<string, unknown> | null;
  responseSchema: Record<string, unknown> | null;
  timeoutMs: number;
  isActive: boolean;
}

export interface AgentLogs {
  conversations: Array<{
    id: string; title: string; userName: string; messageCount: number;
    createdAt: string; updatedAt: string;
  }>;
  toolExecutions: Array<{
    id: string; conversationId: string; toolName: string;
    input: unknown; responseStatus?: number; response?: unknown;
    status: "pending" | "success" | "failure"; errorMessage?: string;
    durationMs?: number; createdAt: string;
  }>;
}

async function api<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  const payload = await response.json().catch(() => ({})) as T & {
    error?: { message?: string };
  };
  if (!response.ok) throw new Error(payload.error?.message ?? `Request failed (${response.status}).`);
  return payload;
}

export const listAgents = (): Promise<{ items: AiAgent[] }> => api("GET", "/agent-admin/agents");
/** Active agents available to every user from the main chat. */
export const listAvailableAgents = (): Promise<{ items: AiAgent[] }> => api("GET", "/agents");
export const createAgent = (input: Omit<AiAgent, "id" | "createdAt" | "updatedAt">): Promise<{ agent: AiAgent }> =>
  api("POST", "/agent-admin/agents", input);
export const updateAgent = (id: string, input: Partial<AiAgent>): Promise<{ agent: AiAgent }> =>
  api("PATCH", `/agent-admin/agents/${id}`, input);

export const listKnowledge = (agentId: string): Promise<{ items: AgentKnowledge[] }> =>
  api("GET", `/agent-admin/agents/${agentId}/knowledge`);
export const createKnowledge = (agentId: string, input: { title: string; content: string }): Promise<{ knowledge: AgentKnowledge }> =>
  api("POST", `/agent-admin/agents/${agentId}/knowledge`, input);
export const deleteKnowledge = (agentId: string, knowledgeId: string): Promise<{ ok: true }> =>
  api("DELETE", `/agent-admin/agents/${agentId}/knowledge/${knowledgeId}`);
/**
 * Upload one knowledge file.
 *
 * The size verdict is the server's (`AI_AGENT_KNOWLEDGE_MAX_BYTES`), so there is
 * no limit duplicated here — but a body refused by a proxy in front of the API
 * never becomes a response at all: `fetch` REJECTS, and the generic catch
 * upstream then showed "Failed to fetch" for what is almost always an oversized
 * file. That case gets its own sentence.
 */
export async function uploadKnowledge(agentId: string, file: File): Promise<{ knowledge: AgentKnowledge }> {
  const body = new FormData();
  body.append("file", file, file.name);
  let response: Response;
  try {
    response = await fetch(`/api/v1/agent-admin/agents/${agentId}/knowledge-file`, { method: "POST", body });
  } catch {
    throw new Error(`Upload of “${file.name}” was cut off before it reached the server. The file is most likely too large — split it into smaller files, or paste the relevant section as a text source.`);
  }
  const payload = await response.json().catch(() => ({})) as { knowledge?: AgentKnowledge; error?: { message?: string } };
  if (!response.ok || !payload.knowledge) throw new Error(payload.error?.message ?? `Upload failed (${response.status}).`);
  return { knowledge: payload.knowledge };
}

export const listTools = (agentId: string): Promise<{ items: AgentTool[] }> =>
  api("GET", `/agent-admin/agents/${agentId}/tools`);
export const createTool = (agentId: string, input: Omit<AgentTool, "id" | "agentId">): Promise<{ tool: AgentTool }> =>
  api("POST", `/agent-admin/agents/${agentId}/tools`, input);
export const updateTool = (agentId: string, toolId: string, input: Partial<AgentTool>): Promise<{ tool: AgentTool }> =>
  api("PATCH", `/agent-admin/agents/${agentId}/tools/${toolId}`, input);
export const deleteTool = (agentId: string, toolId: string): Promise<{ ok: true }> =>
  api("DELETE", `/agent-admin/agents/${agentId}/tools/${toolId}`);
export const fetchAgentLogs = (agentId: string): Promise<AgentLogs> =>
  api("GET", `/agent-admin/agents/${agentId}/logs`);
